<template>
  <div ref="menuRef" class="relative inline-block">
    <Ellipsis
      :size="iconSize"
      :stroke-width="strokeWidth"
      :aria-expanded="isOpen"
      :aria-label="triggerLabel"
      class="mx-5 cursor-pointer text-gray-600 hover:text-gray-900 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 rounded"
      :class="{ 'rotate-90': isOpen }"
      tabindex="0"
      role="button"
      @click="toggleMenu"
      @keydown.enter="toggleMenu"
      @keydown.space.prevent="toggleMenu"
      @keydown.escape="closeMenu"
      @keydown.arrow-down.prevent="focusFirstItem"
    />

    <transition name="fade">
      <div
        v-if="isOpen"
        ref="dropdownRef"
        class="absolute flex flex-col end-12 -top-2 min-w-32 bg-white shadow-lg rounded-md z-50 border border-gray-200 py-1"
        role="menu"
        :aria-labelledby="triggerId"
        @click="handleMenuClick"
        @keydown.escape="closeMenu"
        @keydown.arrow-up.prevent="focusPreviousItem"
        @keydown.arrow-down.prevent="focusNextItem"
        @keydown.tab="closeMenu"
      >
        <slot
          :close-menu="closeMenu"
          :is-open="isOpen"
          :toggle-menu="toggleMenu"
        ></slot>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
// Import Vue Packages
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { Ellipsis } from 'lucide-vue-next'

//  Interface
interface Props {
  iconSize?: number
  strokeWidth?: number
  triggerLabel?: string
  closeOnClick?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  iconSize: 20,
  strokeWidth: 3,
  triggerLabel: 'Open menu',
  closeOnClick: true,
})

interface Emits {
  (e: 'open'): void
  (e: 'close'): void
  (e: 'toggle', isOpen: boolean): void
}

const emit = defineEmits<Emits>()

// Variables
const isOpen = ref(false)
const menuRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const triggerId = ref(
  `dropdown-trigger-${Math.random().toString(36).substr(2, 9)}`,
)

// Functions
const toggleMenu = () => {
  isOpen.value = !isOpen.value
  emit('toggle', isOpen.value)

  if (isOpen.value) {
    emit('open')
    nextTick(() => {
      dropdownRef.value?.focus()
    })
  } else {
    emit('close')
  }
}

const closeMenu = () => {
  if (isOpen.value) {
    isOpen.value = false
    emit('close')
    emit('toggle', false)
  }
}

const openMenu = () => {
  if (!isOpen.value) {
    isOpen.value = true
    emit('open')
    emit('toggle', true)
  }
}

const handleMenuClick = () => {
  if (props.closeOnClick) {
    closeMenu()
  }
}

// Keyboard Navigation Functions
const getFocusableItems = (): HTMLElement[] => {
  if (!dropdownRef.value) return []
  return Array.from(
    dropdownRef.value.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ),
  )
}

const focusFirstItem = () => {
  if (!isOpen.value) {
    openMenu()
    return
  }

  nextTick(() => {
    const items = getFocusableItems()
    if (items.length > 0) {
      items[0].focus()
    }
  })
}

const focusNextItem = () => {
  const items = getFocusableItems()
  const currentIndex = items.findIndex(item => item === document.activeElement)
  const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
  items[nextIndex]?.focus()
}

const focusPreviousItem = () => {
  const items = getFocusableItems()
  const currentIndex = items.findIndex(item => item === document.activeElement)
  const previousIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
  items[previousIndex]?.focus()
}

const handleResize = () => {
  if (isOpen.value) {
    nextTick()
  }
}

onClickOutside(menuRef, () => {
  closeMenu()
})

// Hooks
onMounted(() => {
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

defineExpose({
  closeMenu,
  openMenu,
  toggleMenu,
  isOpen,
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-4px);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: scale(1) translateY(0);
}

/* Focus styles for better accessibility */
.focus\:ring-2:focus {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
}

/* Smooth rotation animation */
.rotate-90 {
  transform: rotate(90deg);
}
</style>
